import type React from 'react';
import { type ReactNode, useCallback, useState } from 'react';
import type { Cell, Row } from '@tanstack/react-table';

import { cn } from '@crossnokaye/ux-common';

import { DateCell, TextCell } from '@/components/data-table/cells';

import { useTable } from '../context';
import type { VirtualItem } from '../hooks';
import type { CellRenderContext, ColumnConfig } from '../types';

// =============================================================================
// Types
// =============================================================================

interface BodyCellProps<TData> {
  cell: Cell<TData, unknown>;
  rowIndex: number;
}

const createCellRenderContext = <TData,>(
  cell: Cell<TData, unknown>,
  rowIndex: number
): CellRenderContext<TData, unknown> => {
  const value = cell.getValue();
  const row = cell.row;

  return {
    value,
    row,
    original: row.original,
    column: cell.column,
    table: cell.getContext().table,
    getValue: () => value,
    rowIndex,
  };
};

// =============================================================================
// Cell Content Renderer
// =============================================================================

const renderCellContent = <TData,>(
  columnConfig: ColumnConfig<TData> | undefined,
  context: CellRenderContext<TData, unknown>
): ReactNode => {
  const value = context.value;

  // Check for custom renderer in column config
  if (columnConfig?.cellRenderer) {
    return columnConfig.cellRenderer(context);
  }

  // Check for cell type
  if (columnConfig?.cellType === 'date') {
    return <DateCell value={value} />;
  }

  if (columnConfig?.cellType === 'text') {
    return <TextCell value={value} />;
  }

  // Check for meta cell renderer (from TanStack column def)
  const metaRenderer = context.column.columnDef.meta?.cellRenderer;
  if (metaRenderer) {
    return metaRenderer(context);
  }

  // Default: render value as string
  return <TextCell value={value} />;
};

// =============================================================================
// Body Cell
// =============================================================================

const BodyCell = <TData,>({
  cell,
  rowIndex,
  cellClassName,
}: BodyCellProps<TData> & {
  cellClassName: string;
}): ReactNode => {
  const { columnConfigs } = useTable<TData>();
  const columnConfig = columnConfigs.find((c) => c.id === cell.column.id);

  // Get pinning info
  const isPinned = columnConfig?.pinned;
  const leftPinnedColumns = columnConfigs.filter((c) => c.pinned === 'left');
  const rightPinnedColumns = columnConfigs.filter((c) => c.pinned === 'right');
  const isLastLeftPinned =
    isPinned === 'left' && leftPinnedColumns[leftPinnedColumns.length - 1]?.id === cell.column.id;
  const isFirstRightPinned = isPinned === 'right' && rightPinnedColumns[0]?.id === cell.column.id;

  // Pinned column styles with proper z-index and borders
  const pinnedStyles = isPinned
    ? {
        position: 'sticky' as const,
        left: isPinned === 'left' ? `${cell.column.getStart() / 16}rem` : undefined,
        right: isPinned === 'right' ? `${cell.column.getStart('right') / 16}rem` : undefined,
        zIndex: isPinned === 'left' ? 20 : 1,
        // Border on last left-pinned or first right-pinned column
        ...(isLastLeftPinned && {
          borderRightWidth: '0.125rem',
          borderRightStyle: 'solid' as const,
          borderRightColor: 'hsl(var(--primary-foreground))',
        }),
        ...(isFirstRightPinned && {
          borderLeftWidth: '0.125rem',
          borderLeftStyle: 'solid' as const,
          borderLeftColor: 'hsl(var(--primary-foreground))',
        }),
      }
    : undefined;

  // Use custom cell renderer from column config
  // Note: TanStack provides a default cell renderer that just returns getValue(),
  // which doesn't handle our custom rendering, so we always use renderCellContent
  const cellContext = createCellRenderContext(cell, rowIndex);
  const cellContent = renderCellContent(columnConfig, cellContext);
  const cellHoverClassName =
    typeof columnConfig?.cellHoverClassName === 'function'
      ? columnConfig.cellHoverClassName(cellContext)
      : columnConfig?.cellHoverClassName;

  return (
    <td
      data-column-id={cell.column.id}
      className={cn(
        'flex truncate items-center px-0 bg-secondary border-b-2 border-primary-foreground',
        isPinned && 'bg-background',
        cellHoverClassName,
        cellClassName
      )}
      style={{
        width: `calc(var(--col-${cell.column.id}-size) / 16 * 1rem)`,
        minWidth: cell.column.columnDef.minSize,
        maxWidth: cell.column.columnDef.maxSize,
        ...pinnedStyles,
      }}
    >
      {cellContent}
    </td>
  );
};

// =============================================================================
// Body Row
// =============================================================================

interface BodyRowProps<TData> {
  row: Row<TData>;
  rowIndex: number;
  style?: React.CSSProperties;
  onRowClicked?: (row: Row<TData>) => void;
}

const BodyRow = <TData,>({
  row,
  rowIndex,
  style,
  onRowClicked,
}: BodyRowProps<TData>): ReactNode => {
  const { styles } = useTable<TData>();
  const isSelected = row.getIsSelected();
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  // Compute cell className based on row state
  const cellClassName = cn(
    styles.row.base,
    isHovered && !isSelected && styles.row.hovered,
    isSelected && styles.row.selected
  );

  return (
    <tr
      className={cn('flex', onRowClicked && 'cursor-pointer')}
      style={style}
      data-component="data-table-row"
      data-index={rowIndex}
      data-row-id={row.id}
      data-selected={isSelected || undefined}
      onClick={(e): void => {
        if (row && e.isTrusted) {
          onRowClicked?.(row);
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {row.getVisibleCells().map((cell) => (
        <BodyCell key={cell.id} cell={cell} rowIndex={rowIndex} cellClassName={cellClassName} />
      ))}
    </tr>
  );
};

// =============================================================================
// Data Table Body
// =============================================================================

interface DataTableBodyProps<TData> {
  rows: Row<TData>[];
  virtualItems: VirtualItem<TData>[];
  totalSize: number;
  isVirtualized: boolean;
  onRowClicked?: (row: Row<TData>) => void;
}

export const DataTableBody = <TData,>({
  rows,
  virtualItems,
  totalSize,
  isVirtualized,
  onRowClicked,
}: DataTableBodyProps<TData>): ReactNode => {
  if (rows.length === 0) {
    return (
      <tbody className="grid">
        <tr className="flex">
          <td colSpan={100} className="h-24 text-center text-muted-foreground flex-1">
            No data available
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody
      className="grid relative"
      style={isVirtualized ? { height: `${totalSize}px` } : undefined}
    >
      {isVirtualized
        ? virtualItems.map(({ index, start, row }) => (
            <BodyRow
              key={row.id}
              row={row}
              rowIndex={index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${start}px)`,
              }}
              onRowClicked={onRowClicked}
            />
          ))
        : rows.map((row, index) => (
            <BodyRow key={row.id} row={row} rowIndex={index} onRowClicked={onRowClicked} />
          ))}
    </tbody>
  );
};

DataTableBody.displayName = 'DataTable.DataTableBody';
